import React from 'react';
import { cn } from '@/lib/utils';

interface SpinnerProps {
    size?: 'sm' | 'md' | 'lg';
    className?: string;
}

export default function Spinner({ size = 'md', className }: SpinnerProps) {
    const sizeClasses = {
        sm: 'w-4 h-4',
        md: 'w-8 h-8',
        lg: 'w-16 h-16'
    };

    const borderClasses = {
        sm: 'border-2',
        md: 'border-4',
        lg: 'border-8'
    };

    return (
        <div
            className={cn(
                'animate-spin rounded-full border-muted-foreground border-t-primary',
                sizeClasses[size],
                borderClasses[size],
                className
            )}
        />
    );
}

interface LoadingSpinnerProps {
    message?: string;
    className?: string;
    size?: 'sm' | 'md' | 'lg';
}

export function LoadingSpinner({
    message,
    className,
    size = 'md'
}: LoadingSpinnerProps) {
    return (
        <div className={cn('flex flex-col items-center justify-center py-12 space-y-4', className)}>
            <Spinner size={size} />
            <p className="text-sm text-muted-foreground">{message}</p>
        </div>
    );
}

export function FullscreenSpinner({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' } = {}) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background">
            <Spinner size={size} />
        </div>
    );
}

// Spinner pequeño pensado para meter dentro de un botón mientras la acción está en curso.
// Hereda el color del texto del botón padre vía `border-current` — el resto del anillo es
// translúcido (`/25`) y el indicador (border-top) full opacity, así rota con contraste claro
// incluso en botones de bajo contraste (texto oscuro sobre blanco). No depende de overrides
// de border globales (`border-current` se aplica con specificity de clase, gana sobre
// `* { @apply border-border }` del globals.css).
export function InlineSpinner({ size = 'sm', className }: { size?: 'sm' | 'md'; className?: string } = {}) {
    const sizeCls = size === 'md' ? 'h-4 w-4' : 'h-3.5 w-3.5';
    return (
        <span
            className={cn('inline-block animate-spin rounded-full border-2 border-current/25 border-t-current', sizeCls, className)}
            aria-hidden="true"
        />
    );
}